import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2,
  Check,
  X,
  Clock,
  MapPin,
  Calendar,
  ExternalLink,
  UserMinus,
  UserPlus,
  Mail,
  Info,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { pl } from "date-fns/locale";

type ProposalRow = {
  id: string;
  shelter_id: string;
  previous_owner_user_id: string;
  previous_owner_email: string | null;
  nominee_email: string | null;
  nominee_user_id: string | null;
  note: string | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  admin_note: string | null;
  created_at: string;
  shelter_name?: string;
  shelter_city?: string;
  shelter_slug?: string;
  nominee_in_team?: boolean;
};

const useSuccessionProposals = () =>
  useQuery({
    queryKey: ["admin-succession-proposals"],
    queryFn: async (): Promise<ProposalRow[]> => {
      const { data: rows, error } = await supabase
        .from("shelter_succession_proposals")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const list = (rows ?? []) as ProposalRow[];
      if (list.length === 0) return [];

      const shelterIds = Array.from(new Set(list.map((r) => r.shelter_id)));
      const { data: shelters } = await supabase
        .from("shelters")
        .select("id, name, city, slug")
        .in("id", shelterIds);
      const sMap = new Map((shelters ?? []).map((s) => [s.id, s]));

      // Check if any nominee_user_id is already in the team for that shelter
      const nomineePairs = list.filter((r) => r.nominee_user_id);
      let teamMemberSet = new Set<string>();
      if (nomineePairs.length > 0) {
        const { data: team } = await supabase
          .from("shelter_owners")
          .select("user_id, shelter_id")
          .in(
            "user_id",
            nomineePairs.map((r) => r.nominee_user_id as string),
          );
        teamMemberSet = new Set(
          (team ?? []).map((t) => `${t.user_id}::${t.shelter_id}`),
        );
      }

      return list.map((r) => ({
        ...r,
        shelter_name: sMap.get(r.shelter_id)?.name,
        shelter_city: sMap.get(r.shelter_id)?.city,
        shelter_slug: sMap.get(r.shelter_id)?.slug,
        nominee_in_team: r.nominee_user_id
          ? teamMemberSet.has(`${r.nominee_user_id}::${r.shelter_id}`)
          : false,
      }));
    },
  });

const StatusBadge = ({ status }: { status: string }) => {
  if (status === "pending")
    return (
      <Badge variant="outline" className="border-orange-300 text-orange-700">
        <Clock className="h-3 w-3 mr-1" />
        Oczekuje
      </Badge>
    );
  if (status === "approved")
    return (
      <Badge variant="outline" className="border-green-300 text-green-700">
        <Check className="h-3 w-3 mr-1" />
        Zatwierdzona
      </Badge>
    );
  return (
    <Badge variant="outline" className="border-red-300 text-red-700">
      <X className="h-3 w-3 mr-1" />
      Odrzucona
    </Badge>
  );
};

export const SuccessionProposalsSection = () => {
  const { data, isLoading, refetch } = useSuccessionProposals();
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  const reviewMutation = useMutation({
    mutationFn: async (args: { proposalId: string; action: "approve" | "reject"; adminNote?: string }) => {
      const { data: resp, error } = await supabase.functions.invoke("review-succession-proposal", {
        body: {
          proposal_id: args.proposalId,
          action: args.action,
          admin_note: args.adminNote,
        },
      });
      if (error) throw error;
      if (resp?.error) throw new Error(resp.error);
      return resp;
    },
    onSuccess: (_resp, vars) => {
      toast.success(vars.action === "approve" ? "Propozycja zatwierdzona, dostęp przyznany" : "Propozycja odrzucona");
      setRejectingId(null);
      setRejectNote("");
      queryClient.invalidateQueries({ queryKey: ["admin-succession-proposals"] });
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
      refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Operacja nie powiodła się"),
  });

  if (isLoading) {
    return null;
  }

  const all = data ?? [];
  const pendingCount = all.filter((r) => r.status === "pending").length;
  const filtered = filter === "pending" ? all.filter((r) => r.status === "pending") : all;

  if (all.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-semibold text-base">
            Propozycje następcy po rezygnacji
          </h3>
          {pendingCount > 0 && (
            <Badge variant="outline" className="border-orange-300 text-orange-700">
              {pendingCount} oczekujących
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={filter === "pending" ? "default" : "outline"}
            onClick={() => setFilter("pending")}
          >
            Oczekujące ({pendingCount})
          </Button>
          <Button
            size="sm"
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
          >
            Wszystkie ({all.length})
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Brak propozycji do wyświetlenia.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const hasAccount = !!row.nominee_user_id;
            const inTeam = !!row.nominee_in_team;
            return (
              <Card key={row.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                        {row.shelter_name ?? "Nieznane schronisko"}
                        <StatusBadge status={row.status} />
                      </CardTitle>
                      <CardDescription className="flex items-center gap-3 flex-wrap mt-1">
                        {row.shelter_city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {row.shelter_city}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(row.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                        </span>
                        {row.shelter_slug && (
                          <Link
                            to={`/schronisko/${row.shelter_slug}`}
                            target="_blank"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" /> Profil
                          </Link>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-md bg-muted/40 p-3 space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <UserMinus className="h-3 w-3" /> Poprzedni właściciel
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="break-all">{row.previous_owner_email ?? "-"}</span>
                      </div>
                    </div>
                    <div className="rounded-md bg-muted/40 p-3 space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <UserPlus className="h-3 w-3" /> Proponowany następca
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="break-all">{row.nominee_email ?? "-"}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 pt-1">
                        {inTeam ? (
                          <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                            jest w zespole
                          </Badge>
                        ) : hasAccount ? (
                          <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                            ma konto
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
                            brak konta
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {row.note && (
                    <div className="text-sm">
                      <div className="font-medium text-xs text-muted-foreground mb-1">
                        Wiadomość od poprzedniego właściciela:
                      </div>
                      <div className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-sm">
                        {row.note}
                      </div>
                    </div>
                  )}

                  {row.admin_note && (
                    <div className="text-sm">
                      <div className="font-medium text-xs text-muted-foreground mb-1">
                        Notatka admina:
                      </div>
                      <div className="rounded-md bg-muted/40 p-2 text-xs">{row.admin_note}</div>
                    </div>
                  )}

                  {row.status === "pending" && !hasAccount && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm">
                      <Info className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                      <p className="text-amber-900">
                        Następca nie ma jeszcze konta. Poproś go o rejestrację na <strong>/panel</strong> tym samym adresem email i zatwierdź ponownie - przycisk uaktywni się automatycznie.
                      </p>
                    </div>
                  )}

                  {row.status === "pending" && (
                    <>
                      {rejectingId === row.id ? (
                        <div className="space-y-2 border-t pt-3">
                          <Textarea
                            placeholder="Powód odrzucenia (opcjonalnie)"
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            maxLength={500}
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                reviewMutation.mutate({
                                  proposalId: row.id,
                                  action: "reject",
                                  adminNote: rejectNote || undefined,
                                })
                              }
                              disabled={reviewMutation.isPending}
                            >
                              {reviewMutation.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                              Potwierdź odrzucenie
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setRejectingId(null);
                                setRejectNote("");
                              }}
                            >
                              Anuluj
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 border-t pt-3 flex-wrap">
                          <Button
                            size="sm"
                            onClick={() => reviewMutation.mutate({ proposalId: row.id, action: "approve" })}
                            disabled={reviewMutation.isPending || !hasAccount}
                          >
                            {reviewMutation.isPending ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Check className="h-3 w-3 mr-1" />
                            )}
                            Zatwierdź jako właściciela
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRejectingId(row.id)}
                          >
                            <X className="h-3 w-3 mr-1" /> Odrzuć
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};
